import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getMe();
        setUser(response.data.user);
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const googleLogin = async (credential) => {
    const response = await authAPI.googleAuth(credential);
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await authAPI.register({ name, email, password });
    localStorage.setItem('token', response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
  };

  const deleteAccount = async () => {
    await authAPI.deleteAccount();
    localStorage.removeItem('token');
    setUser(null);
  };

  // Trial days remaining (null if not on trial or expiry not enforced)
  const trialDaysRemaining = (() => {
    if (!user || user.plan !== 'trial' || !user.trial_started_at) return null;
    const elapsed = (Date.now() - new Date(user.trial_started_at).getTime()) / (1000 * 60 * 60 * 24);
    return Math.max(0, Math.ceil(14 - elapsed));
  })();

  const updateUser = (updatedFields) => {
    setUser(prev => prev ? { ...prev, ...updatedFields } : prev);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await authAPI.getMe();
      setUser(response.data.user);
    } catch {
      // non-fatal
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, register, googleLogin, logout, deleteAccount, updateUser, refreshUser, loading, trialDaysRemaining }}>
      {children}
    </AuthContext.Provider>
  );
};
